/**
 * Financial Forecast Q&A Page - Gap-filling questions for financial forecast
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import financialForecastService, { ForecastQuestion } from '../services/financialForecastService';

const FinancialForecastQAPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();

  const [currentQuestion, setCurrentQuestion] = useState<ForecastQuestion | null>(null);
  const [answer, setAnswer] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startQA = async (retryCount = 0) => {
    try {
      setError(null);
      setLoading(true);
      await financialForecastService.startQA(sessionId!);
      await loadCurrentQuestion();
    } catch (err: any) {
      if (retryCount < 1) {
        // Retry once — handles Render cold start timeouts
        await startQA(retryCount + 1);
        return;
      }
      setError(err.response?.data?.detail || 'Failed to start financial forecast Q&A');
      setLoading(false);
    }
  };

  const loadCurrentQuestion = async () => {
    try {
      const question = await financialForecastService.getCurrentQuestion(sessionId!);

      if (question.all_complete) {
        await generateForecast();
        return;
      }

      setCurrentQuestion(question);
      setLoading(false);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load question');
      setLoading(false);
    }
  };

  const generateForecast = async () => {
    setGenerating(true);
    try {
      await financialForecastService.generate(sessionId!);
      navigate(`/financial-forecast/${sessionId}`);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to generate financial forecast');
      setGenerating(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        const question = await financialForecastService.getCurrentQuestion(sessionId!);
        if (question.all_complete && question.total_questions > 0) {
          await generateForecast();
        } else if (question.question) {
          setCurrentQuestion(question);
          setLoading(false);
        } else {
          await startQA();
        }
      } catch {
        await startQA();
      }
    };
    init();
  }, [sessionId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!answer.trim()) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await financialForecastService.submitAnswer(sessionId!, answer.trim());
      setAnswer('');

      if (response.all_complete) {
        await generateForecast();
      } else {
        await loadCurrentQuestion();
      }

      setIsSubmitting(false);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to submit answer');
      setIsSubmitting(false);
    }
  };

  if (generating) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{
          padding: '40px',
          backgroundColor: '#e8f5e9',
          border: '2px solid #4caf50',
          borderRadius: '8px',
          maxWidth: '600px',
          margin: '0 auto',
        }}>
          <h2 style={{ fontSize: '2rem', marginBottom: '16px' }}>Generating Financial Forecast...</h2>
          <p style={{ fontSize: '1.1rem', color: '#666' }}>
            Building your financial projections spreadsheet with revenue, costs, cash flow, and P&L. This may take a moment.
          </p>
          <Loader2 size={40} style={{ marginTop: '24px', animation: 'spin 1s linear infinite' }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h2>Loading financial forecast questions...</h2>
      </div>
    );
  }

  if (error && !currentQuestion) {
    return (
      <div style={{ padding: '40px', maxWidth: '800px', margin: '0 auto' }}>
        <div style={{
          padding: '20px',
          backgroundColor: '#f8d7da',
          color: '#721c24',
          border: '1px solid #f5c6cb',
          borderRadius: '4px',
        }}>
          <h3>Error</h3>
          <p>{error}</p>
          <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
            <button
              onClick={() => startQA()}
              style={{
                padding: '10px 20px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              Try Again
            </button>
            <button
              onClick={() => navigate(`/business-plan/${sessionId}`)}
              style={{
                padding: '10px 20px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              Back to Business Plan
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '40px', maxWidth: '900px', margin: '0 auto' }}>
      {/* Progress Header */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h1 style={{ fontSize: '2rem', margin: 0 }}>Financial Forecast — Additional Information</h1>
          <div style={{
            padding: '8px 16px',
            backgroundColor: '#28a745',
            color: 'white',
            borderRadius: '20px',
            fontSize: '0.9rem',
            fontWeight: 'bold',
          }}>
            Question {currentQuestion?.question_number} of {currentQuestion?.total_questions}
          </div>
        </div>

        {/* Progress bar */}
        <div style={{ backgroundColor: '#e9ecef', borderRadius: '4px', height: '8px' }}>
          <div style={{
            backgroundColor: '#28a745',
            borderRadius: '4px',
            height: '8px',
            width: `${((currentQuestion?.question_number || 0) / (currentQuestion?.total_questions || 1)) * 100}%`,
            transition: 'width 0.3s ease',
          }} />
        </div>
      </div>

      {/* Question */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{
          padding: '24px',
          backgroundColor: '#f8f9fa',
          border: '2px solid #dee2e6',
          borderRadius: '8px',
        }}>
          <div style={{ fontSize: '0.9rem', color: '#6c757d', marginBottom: '8px', fontWeight: 'bold' }}>
            QUESTION
          </div>
          <p style={{ fontSize: '1.2rem', margin: 0, lineHeight: '1.6' }}>
            {currentQuestion?.question}
          </p>
        </div>
      </div>

      {/* Answer Form */}
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px', fontSize: '1.1rem' }}>
            Your Answer
          </label>
          <textarea
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            placeholder="Provide financial details, estimates, and assumptions..."
            style={{
              width: '100%',
              minHeight: '180px',
              padding: '16px',
              fontSize: '1rem',
              border: '2px solid #ced4da',
              borderRadius: '4px',
              fontFamily: 'inherit',
              resize: 'vertical',
            }}
          />
        </div>

        {error && (
          <div style={{
            padding: '12px',
            backgroundColor: '#f8d7da',
            color: '#721c24',
            border: '1px solid #f5c6cb',
            borderRadius: '4px',
            marginBottom: '16px',
          }}>
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={isSubmitting || !answer.trim()}
          style={{
            width: '100%',
            padding: '16px',
            fontSize: '1.1rem',
            backgroundColor: isSubmitting || !answer.trim() ? '#ccc' : '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: isSubmitting || !answer.trim() ? 'not-allowed' : 'pointer',
            fontWeight: 'bold',
          }}
        >
          {isSubmitting ? 'Submitting...' : 'Submit Answer & Continue'}
        </button>
      </form>
    </div>
  );
};

export default FinancialForecastQAPage;
